import * as THREE from "three";
import { PALETTE } from "./config";

function noise(ctx: CanvasRenderingContext2D, w: number, h: number, alpha: number): void {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * 255 * alpha;
    d[i] = Math.min(255, Math.max(0, d[i] + n));
    d[i + 1] = Math.min(255, Math.max(0, d[i + 1] + n));
    d[i + 2] = Math.min(255, Math.max(0, d[i + 2] + n));
  }
  ctx.putImageData(img, 0, 0);
}

function canvasTexture(
  size: number,
  paint: (ctx: CanvasRenderingContext2D, size: number) => void,
): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("2d context");
  paint(ctx, size);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

export function makeConcreteTexture(): THREE.CanvasTexture {
  return canvasTexture(512, (ctx, s) => {
    ctx.fillStyle = "#c7c2b8";
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 900; i++) {
      const g = 150 + Math.random() * 50;
      ctx.fillStyle = `rgba(${g},${g - 4},${g - 12},${0.08 + Math.random() * 0.12})`;
      ctx.fillRect(Math.random() * s, Math.random() * s, 2 + Math.random() * 6, 1 + Math.random() * 3);
    }
    noise(ctx, s, s, 0.08);
  });
}

export function makeSandTexture(): THREE.CanvasTexture {
  return canvasTexture(512, (ctx, s) => {
    ctx.fillStyle = "#c4b18a";
    ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 1400; i++) {
      ctx.fillStyle = `rgba(${180 + Math.random() * 50},${150 + Math.random() * 40},${90 + Math.random() * 40},0.18)`;
      ctx.beginPath();
      ctx.arc(Math.random() * s, Math.random() * s, Math.random() * 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    noise(ctx, s, s, 0.1);
  });
}

export function makeTeakTexture(): THREE.CanvasTexture {
  return canvasTexture(256, (ctx, s) => {
    const plank = 18;
    for (let y = 0; y < s; y += plank) {
      const tone = 118 + Math.random() * 28;
      ctx.fillStyle = `rgb(${tone + 20},${tone - 20},${tone - 70})`;
      ctx.fillRect(0, y, s, plank - 1);
      ctx.fillStyle = "rgba(40,20,8,0.35)";
      ctx.fillRect(0, y + plank - 1, s, 1);
    }
    noise(ctx, s, s, 0.06);
  });
}

export function makeCausticsTexture(): THREE.CanvasTexture {
  return canvasTexture(256, (ctx, s) => {
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = "rgba(180,255,240,0.55)";
    ctx.lineWidth = 1.2;
    for (let i = 0; i < 28; i++) {
      ctx.beginPath();
      const y = (i / 28) * s;
      for (let x = 0; x <= s; x += 8) {
        const yy = y + Math.sin(x * 0.08 + i) * 10 + Math.cos(x * 0.03 + i * 0.4) * 6;
        if (x === 0) ctx.moveTo(x, yy);
        else ctx.lineTo(x, yy);
      }
      ctx.stroke();
    }
  });
}

export function makeBurgeeTexture(): THREE.CanvasTexture {
  const tex = canvasTexture(256, (ctx, s) => {
    ctx.fillStyle = "#1b365d";
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(s, s * 0.5);
    ctx.lineTo(0, s);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "#e85a24";
    ctx.lineWidth = 18;
    ctx.beginPath();
    ctx.moveTo(s * 0.18, s * 0.72);
    ctx.lineTo(s * 0.42, s * 0.28);
    ctx.lineTo(s * 0.52, s * 0.52);
    ctx.stroke();
  });
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

export function makeBerthTexture(label: string): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 128;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("2d");
  ctx.fillStyle = "#1b2429";
  ctx.fillRect(0, 0, 256, 128);
  ctx.strokeStyle = "#e85a24";
  ctx.lineWidth = 8;
  ctx.strokeRect(8, 8, 240, 112);
  ctx.fillStyle = "#f4f7f8";
  ctx.font = "700 64px Syne, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, 128, 70);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function makeTransomName(name: string): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = 512;
  c.height = 96;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("2d");
  ctx.clearRect(0, 0, 512, 96);
  ctx.fillStyle = "#1b365d";
  ctx.font = "700 48px Syne, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(name, 256, 52);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export type ClubMaterials = ReturnType<typeof createMaterials>;

export function createMaterials(): {
  concrete: THREE.MeshStandardMaterial;
  whiteConcrete: THREE.MeshStandardMaterial;
  darkConcrete: THREE.MeshStandardMaterial;
  sand: THREE.MeshStandardMaterial;
  wetSand: THREE.MeshStandardMaterial;
  glass: THREE.MeshPhysicalMaterial;
  darkGlass: THREE.MeshPhysicalMaterial;
  steel: THREE.MeshStandardMaterial;
  chrome: THREE.MeshStandardMaterial;
  yachtWhite: THREE.MeshStandardMaterial;
  teak: THREE.MeshStandardMaterial;
  palmTrunk: THREE.MeshStandardMaterial;
  frond: THREE.MeshStandardMaterial;
  rock: THREE.MeshStandardMaterial;
  coral: THREE.MeshStandardMaterial;
  navy: THREE.MeshStandardMaterial;
  cushion: THREE.MeshStandardMaterial;
  waterline: THREE.MeshStandardMaterial;
  caustics: THREE.MeshBasicMaterial;
  pool: THREE.MeshPhysicalMaterial;
  interior: THREE.MeshStandardMaterial;
  white: THREE.MeshStandardMaterial;
  grass: THREE.MeshStandardMaterial;
  asphalt: THREE.MeshStandardMaterial;
  stucco: THREE.MeshStandardMaterial;
  roofTerra: THREE.MeshStandardMaterial;
  roofNavy: THREE.MeshStandardMaterial;
  canopy: THREE.MeshStandardMaterial;
} {
  const concreteMap = makeConcreteTexture();
  concreteMap.repeat.set(8, 8);
  const sandMap = makeSandTexture();
  sandMap.repeat.set(12, 12);
  const teakMap = makeTeakTexture();
  teakMap.repeat.set(2, 8);
  const causticsMap = makeCausticsTexture();
  causticsMap.repeat.set(6, 6);

  return {
    concrete: new THREE.MeshStandardMaterial({
      color: PALETTE.concrete,
      map: concreteMap,
      roughness: 0.82,
      metalness: 0.02,
    }),
    whiteConcrete: new THREE.MeshStandardMaterial({
      color: PALETTE.whiteConcrete,
      roughness: 0.62,
      metalness: 0.04,
    }),
    darkConcrete: new THREE.MeshStandardMaterial({
      color: 0x8d8880,
      roughness: 0.78,
      metalness: 0.03,
    }),
    sand: new THREE.MeshStandardMaterial({
      color: PALETTE.sand,
      map: sandMap,
      roughness: 1,
      metalness: 0,
    }),
    wetSand: new THREE.MeshStandardMaterial({
      color: PALETTE.wetSand,
      roughness: 0.55,
      metalness: 0.02,
    }),
    glass: new THREE.MeshPhysicalMaterial({
      color: PALETTE.glass,
      metalness: 0.05,
      roughness: 0.05,
      transmission: 0.78,
      thickness: 0.6,
      ior: 1.45,
      transparent: true,
      opacity: 0.55,
      envMapIntensity: 1.4,
    }),
    darkGlass: new THREE.MeshPhysicalMaterial({
      color: 0x1a2e38,
      metalness: 0.2,
      roughness: 0.08,
      transparent: true,
      opacity: 0.72,
      envMapIntensity: 1.2,
    }),
    steel: new THREE.MeshStandardMaterial({
      color: PALETTE.steel,
      roughness: 0.35,
      metalness: 0.7,
    }),
    chrome: new THREE.MeshStandardMaterial({
      color: 0xc5cdd1,
      roughness: 0.18,
      metalness: 0.95,
    }),
    yachtWhite: new THREE.MeshStandardMaterial({
      color: PALETTE.yachtWhite,
      roughness: 0.28,
      metalness: 0.08,
      envMapIntensity: 0.9,
    }),
    teak: new THREE.MeshStandardMaterial({
      color: PALETTE.teak,
      map: teakMap,
      roughness: 0.7,
      metalness: 0,
    }),
    palmTrunk: new THREE.MeshStandardMaterial({
      color: 0x7a5a3a,
      roughness: 0.9,
      metalness: 0,
    }),
    frond: new THREE.MeshStandardMaterial({
      color: PALETTE.frond,
      roughness: 0.82,
      metalness: 0,
      side: THREE.DoubleSide,
    }),
    rock: new THREE.MeshStandardMaterial({
      color: PALETTE.rock,
      roughness: 0.92,
      metalness: 0.04,
    }),
    coral: new THREE.MeshStandardMaterial({
      color: PALETTE.coral,
      roughness: 0.55,
      metalness: 0.05,
    }),
    navy: new THREE.MeshStandardMaterial({
      color: PALETTE.navy,
      roughness: 0.4,
      metalness: 0.1,
    }),
    cushion: new THREE.MeshStandardMaterial({
      color: PALETTE.cushion,
      roughness: 0.7,
      metalness: 0,
    }),
    waterline: new THREE.MeshStandardMaterial({
      color: 0x0e2430,
      roughness: 0.45,
      metalness: 0.15,
    }),
    caustics: new THREE.MeshBasicMaterial({
      map: causticsMap,
      transparent: true,
      opacity: 0.28,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
    pool: new THREE.MeshPhysicalMaterial({
      color: 0x3ad1c8,
      roughness: 0.05,
      metalness: 0,
      transmission: 0.55,
      thickness: 0.8,
      transparent: true,
      opacity: 0.85,
    }),
    interior: new THREE.MeshStandardMaterial({
      color: 0xd8cfc2,
      roughness: 0.7,
      metalness: 0,
    }),
    white: new THREE.MeshStandardMaterial({
      color: 0xf7f8f4,
      roughness: 0.45,
      metalness: 0.04,
    }),
    grass: new THREE.MeshStandardMaterial({
      color: 0x3d7a52,
      roughness: 0.95,
      metalness: 0,
    }),
    asphalt: new THREE.MeshStandardMaterial({
      color: 0x4a4d50,
      roughness: 0.88,
      metalness: 0.04,
    }),
    stucco: new THREE.MeshStandardMaterial({
      color: 0xefe6d6,
      roughness: 0.78,
      metalness: 0.02,
    }),
    roofTerra: new THREE.MeshStandardMaterial({
      color: 0xb85a38,
      roughness: 0.72,
      metalness: 0.04,
    }),
    roofNavy: new THREE.MeshStandardMaterial({
      color: 0x2a4050,
      roughness: 0.55,
      metalness: 0.08,
    }),
    canopy: new THREE.MeshStandardMaterial({
      color: 0x2f6b3c,
      roughness: 0.9,
      metalness: 0,
    }),
  };
}
